import { useEffect, useRef } from 'react';
import { DigitalFormScopeHooks } from '@frontend/digital-forms-scope';
import { SEND_FORM_STEPS } from '../../constants/steps';
import { SendFormsOrigin } from '../../types';
import { useSendFormsStore } from '../store';

interface UseStepsConfigProps {
  origin: SendFormsOrigin;
  locationIds: string[];
}

/**
 * Resolves the step configuration for a given origin and location count.
 */
function getStepsForOrigin(origin: SendFormsOrigin, hasMultipleLocations: boolean) {
  switch (origin) {
    case SendFormsOrigin.FORMS:
      return hasMultipleLocations ? SEND_FORM_STEPS.formsOriginWithMultipleLocations : SEND_FORM_STEPS.formsOrigin;

    case SendFormsOrigin.CONTACTS:
      return hasMultipleLocations
        ? SEND_FORM_STEPS.contactsOriginWithMultipleLocations
        : SEND_FORM_STEPS.contactsOrigin;

    case SendFormsOrigin.COMPLETE:
      return hasMultipleLocations
        ? SEND_FORM_STEPS.completeOriginWithMultipleLocations
        : SEND_FORM_STEPS.completeOrigin;

    case SendFormsOrigin.KIOSK:
      return hasMultipleLocations ? SEND_FORM_STEPS.kioskOriginWithMultipleLocations : SEND_FORM_STEPS.kioskOrigin;

    case SendFormsOrigin.MESSAGING:
      return hasMultipleLocations
        ? SEND_FORM_STEPS.messagingOriginWithMultipleLocations
        : SEND_FORM_STEPS.messagingOrigin;

    default:
      return [];
  }
}

const useStepsConfig = ({ origin, locationIds }: UseStepsConfigProps) => {
  const { setCurrentStep, setSteps, setFormsEnabledLocationIds, setLocationId, setSelectedDeliveryMethod } =
    useSendFormsStore([
      'setCurrentStep',
      'setSteps',
      'setFormsEnabledLocationIds',
      'setLocationId',
      'setSelectedDeliveryMethod',
    ]);
  const { isLoadingFormsEnabledOrgLocations, formsEnabledLocalLocationIds } =
    DigitalFormScopeHooks.useFormsEnabledOrgLocations({ localLocationIds: locationIds });

  // Track whether steps have been initialized to prevent resetting currentStep
  // on subsequent re-renders caused by new formsEnabledLocalLocationIds references
  // (e.g., from parent re-renders creating new locationIds arrays).
  const isStepsInitializedRef = useRef(false);
  const previousLocationCountRef = useRef<number | null>(null);

  useEffect(() => {
    if (isLoadingFormsEnabledOrgLocations) {
      return;
    }

    const hasMultipleLocations = formsEnabledLocalLocationIds.length > 1;
    const locationCount = formsEnabledLocalLocationIds.length;
    const steps = getStepsForOrigin(origin, hasMultipleLocations);

    // Only set steps and reset currentStep on initial config or when the
    // location count actually changes (which changes the step sequence).
    // This prevents resetting to step 1 when formsEnabledLocalLocationIds
    // gets a new array reference but the same content.
    const locationCountChanged = previousLocationCountRef.current !== locationCount;

    if (!isStepsInitializedRef.current || locationCountChanged) {
      setSteps(steps);
      setCurrentStep(steps[0]);

      if (origin === SendFormsOrigin.KIOSK) {
        setSelectedDeliveryMethod('kiosk');
      }

      isStepsInitializedRef.current = true;
      previousLocationCountRef.current = locationCount;
    }

    setFormsEnabledLocationIds(formsEnabledLocalLocationIds);
    if (formsEnabledLocalLocationIds.length) {
      setLocationId(formsEnabledLocalLocationIds[0]);
    }
  }, [
    origin,
    isLoadingFormsEnabledOrgLocations,
    formsEnabledLocalLocationIds,
    setFormsEnabledLocationIds,
    setSteps,
    setCurrentStep,
    setSelectedDeliveryMethod,
    setLocationId,
  ]);
};

export default useStepsConfig;
